import { useIsRouting } from "@solidjs/router"
import { createEffect, onCleanup } from "solid-js"

/** `href`, an app path, with the query parameter `name` set to `value`; its other parameters and hash stay. */
export function withLinkParam(href: string, name: string, value: string) {
  const hashAt = href.includes("#") ? href.indexOf("#") : href.length
  const queryAt = href.slice(0, hashAt).includes("?") ? href.indexOf("?") : hashAt
  const params = new URLSearchParams(href.slice(queryAt + 1, hashAt))
  params.set(name, value)
  return `${href.slice(0, queryAt)}?${params}${href.slice(hashAt)}`
}

/**
 * Rewrites this page's address without the query parameters `drop` names, then
 * with `add`, encoded pairs. Other parameters keep their encoding.
 */
function rewriteSearch(drop: (name: string) => boolean, add = "") {
  const pairs = location.search.slice(1).split("&").filter((pair) => pair && !drop(new URLSearchParams(pair).keys().next().value ?? ""))
  if (add) pairs.push(add)
  const search = pairs.length ? `?${pairs.join("&")}` : ""
  if (search !== location.search) history.replaceState(history.state, "", `${location.pathname}${search}${location.hash}`)
}

/**
 * Removes the query parameter `name` from this page's address and returns its
 * value, or null when the address has none. Call before the router reads the
 * location, so no route or resume point keeps the parameter.
 */
export function takeLinkParam(name: string) {
  const value = new URLSearchParams(location.search).get(name)
  if (value === null) return null
  rewriteSearch((key) => key === name)
  return value
}

/** Query parameters that describe this tab rather than a page, by name. */
const tabParams = new Map<string, string>()

/**
 * Like `takeLinkParam`, but the parameter belongs to the tab: `useTabParams`
 * keeps it in the address for the tab's life. A browser that reopens the tab
 * after a restart, possibly without its session storage, still has it.
 */
export function takeTabParam(name: string) {
  const value = takeLinkParam(name)
  if (value !== null) tabParams.set(name, value)
  return value
}

/** Adds this tab's parameters to the address, or removes them. */
function showTabParams(show: boolean) {
  rewriteSearch((name) => tabParams.has(name), show ? new URLSearchParams([...tabParams]).toString() : "")
}

/**
 * Keeps this tab's parameters (`takeTabParam`) in the address through in-app
 * navigation while the router never reads them, so routes and links stay
 * unchanged. Call once in the router's root.
 */
export function useTabParams() {
  if (tabParams.size === 0) return
  const routing = useIsRouting()
  // The router writes a navigation's address just before it stops routing.
  createEffect(() => {
    if (!routing()) showTabParams(true)
  })
  // Back and forward: the router reads the address in its own popstate
  // listener. The capturing listener runs before it; the bubbling one, added
  // after the router's, runs after it.
  const hide = () => showTabParams(false)
  const show = () => showTabParams(true)
  window.addEventListener("popstate", hide, true)
  window.addEventListener("popstate", show)
  onCleanup(() => {
    window.removeEventListener("popstate", hide, true)
    window.removeEventListener("popstate", show)
  })
}
